/**
 * @reflecto/core — crypto.ts
 *
 * All encryption/decryption for Reflecto lives here.
 * Uses TweetNaCl (X25519 + XSalsa20-Poly1305) to be byte-identical
 * with Lazysodium on Android.
 *
 * RULE: server never sees plaintext. Encrypt before sending, decrypt after receiving.
 */

import nacl from "tweetnacl";
import { decodeBase64, encodeBase64 } from "tweetnacl-util";

export const WIRE_VERSION = 0x01;

export interface KeyPair {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
}

/**
 * Generate a new X25519 keypair.
 */
export function generateKeyPair(): KeyPair {
  return nacl.box.keyPair();
}

/**
 * Encode a Uint8Array to base64 string (for storage/transport).
 */
export { decodeBase64, encodeBase64 };

/**
 * Encrypt a message from sender → recipient.
 *
 * Wire format: [0x01 (1 byte)] + [nonce (24 bytes)] + [ciphertext (variable)]
 *
 * @param plaintext   - raw bytes to encrypt
 * @param recipientPublicKey - recipient's X25519 public key
 * @param senderSecretKey    - sender's X25519 secret key
 * @param nonce              - 24-byte nonce (pass nacl.randomBytes(24) in production)
 */
export function encryptMessage(
  plaintext: Uint8Array,
  recipientPublicKey: Uint8Array,
  senderSecretKey: Uint8Array,
  nonce: Uint8Array = nacl.randomBytes(nacl.box.nonceLength)
): Uint8Array {
  if (recipientPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error(
      `Invalid public key: expected ${nacl.box.publicKeyLength} bytes, got ${recipientPublicKey.length}`
    );
  }
  if (senderSecretKey.length !== nacl.box.secretKeyLength) {
    throw new Error(
      `Invalid secret key: expected ${nacl.box.secretKeyLength} bytes, got ${senderSecretKey.length}`
    );
  }
  if (nonce.length !== nacl.box.nonceLength) {
    throw new Error(`Invalid nonce: expected ${nacl.box.nonceLength} bytes, got ${nonce.length}`);
  }

  const ciphertext = nacl.box(plaintext, nonce, recipientPublicKey, senderSecretKey);

  if (!ciphertext) {
    throw new Error("Encryption failed");
  }

  // Wire format: version (1) + nonce (24) + ciphertext
  const wire = new Uint8Array(1 + nonce.length + ciphertext.length);
  wire[0] = WIRE_VERSION;
  wire.set(nonce, 1);
  wire.set(ciphertext, 1 + nonce.length);
  return wire;
}

/**
 * Decrypt a wire-format blob.
 *
 * @param wire               - [0x01] + [nonce (24)] + [ciphertext]
 * @param senderPublicKey    - sender's X25519 public key
 * @param recipientSecretKey - recipient's X25519 secret key
 */
export function decryptMessage(
  wire: Uint8Array,
  senderPublicKey: Uint8Array,
  recipientSecretKey: Uint8Array
): Uint8Array {
  // version(1) + nonce(24) + poly1305 MAC(16) = 41 bytes minimum
  const MIN_WIRE_LENGTH = 1 + nacl.box.nonceLength + nacl.box.overheadLength;
  if (wire.length < MIN_WIRE_LENGTH) {
    throw new Error(
      `Wire blob too short: expected at least ${MIN_WIRE_LENGTH} bytes, got ${wire.length}`
    );
  }
  if (wire[0] !== WIRE_VERSION) {
    throw new Error(`Unsupported wire version: 0x${wire[0].toString(16)}`);
  }
  if (senderPublicKey.length !== nacl.box.publicKeyLength) {
    throw new Error(
      `Invalid public key: expected ${nacl.box.publicKeyLength} bytes, got ${senderPublicKey.length}`
    );
  }
  if (recipientSecretKey.length !== nacl.box.secretKeyLength) {
    throw new Error(
      `Invalid secret key: expected ${nacl.box.secretKeyLength} bytes, got ${recipientSecretKey.length}`
    );
  }

  const nonce = wire.slice(1, 1 + nacl.box.nonceLength);
  const ciphertext = wire.slice(1 + nacl.box.nonceLength);

  const plaintext = nacl.box.open(ciphertext, nonce, senderPublicKey, recipientSecretKey);

  if (!plaintext) {
    throw new Error("Decryption failed — wrong keys or tampered ciphertext");
  }

  return plaintext;
}
